import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * HeroShowcase — 30-second auto-playing motion showcase alternating RIF and
 * RPL Deep Clinical Review screenshots, with an optional female-voice
 * narration via the browser SpeechSynthesis API (tap-to-play, per autoplay
 * policies).  No mp4, no external voice API required.
 */
const SLIDES = [
  {
    src: "/showcase/rif-01-header.jpg",
    caption: "RIF workflow · deep clinical review",
    module: "RIF",
  },
  {
    src: "/showcase/01-review-header.jpg",
    caption: "Same 8-section rigor for RPL",
    module: "RPL",
  },
  {
    src: "/showcase/rif-02-classification.jpg",
    caption: "Case snapshot · mixed-factor RIF · KIR-AA suspected",
    module: "RIF",
  },
  {
    src: "/showcase/04-priority-table.jpg",
    caption: "Priority problems · GRADE-graded evidence",
    module: "RPL",
  },
  {
    src: "/showcase/rif-04-plan.jpg",
    caption: "Stepwise plan · Th1-Th2, IVIG, LMWH — dosed",
    module: "RIF",
  },
  {
    src: "/showcase/06-management-plan.jpg",
    caption: "Recommended · Consider selectively · Not routine",
    module: "RPL",
  },
];

const SLIDE_MS = 5000; // 6 slides × 5s = 30s loop

const NARRATION = (
  "RIF Master is a precision decision-support platform for reproductive medicine. " +
  "In under two minutes it turns a doctor's inputs — for recurrent implantation failure or recurrent pregnancy loss — " +
  "into a full eight-section clinical review. " +
  "Every case is classified, priority problems are ranked with graded evidence, " +
  "and management is separated into what is recommended, what to consider selectively, and what to avoid. " +
  "Even the most complex immunology cases — KIR-HLA mismatch, T-helper imbalance, obstetric antiphospholipid syndrome — " +
  "receive a personalised, dosed plan that clinicians can trust. " +
  "Grounded on the latest ASRM, ESHRE, ASRI and RCOG guidelines."
);

function pickFemaleVoice() {
  if (!("speechSynthesis" in window)) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;
  // Preferred voices in order — most-natural female voice on each platform
  const preferred = [
    /Samantha/i, /Karen/i, /Victoria/i, /Serena/i,      // iOS / macOS
    /Google.*(US|UK).*English.*Female/i,
    /Microsoft (Aria|Jenny|Sonia|Zira|Libby|Emma)/i,   // Windows
    /Female/i, /en-(US|GB|AU|IN).*(?!male)/i,
  ];
  for (const p of preferred) {
    const v = voices.find((x) => p.test(x.name));
    if (v) return v;
  }
  // Last resort: first English voice
  return voices.find((v) => /^en/i.test(v.lang)) || voices[0];
}

export function HeroShowcase() {
  const [active, setActive] = useState(0);
  const [audioOn, setAudioOn] = useState(false);
  const utteranceRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % SLIDES.length), SLIDE_MS);
    return () => clearInterval(t);
  }, []);

  // Speech synthesis needs voices loaded — Chrome loads them async.
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const s = window.speechSynthesis;
    if (s.getVoices().length === 0) {
      s.onvoiceschanged = () => { /* no-op — pickFemaleVoice() reads at play time */ };
    }
    return () => { s.cancel(); };
  }, []);

  const toggleAudio = () => {
    if (!("speechSynthesis" in window)) return;
    const s = window.speechSynthesis;
    if (audioOn) {
      s.cancel();
      setAudioOn(false);
      return;
    }
    // Kick off narration on user gesture (autoplay policy)
    s.cancel();
    const u = new SpeechSynthesisUtterance(NARRATION);
    const v = pickFemaleVoice();
    if (v) u.voice = v;
    u.rate = 1.0;
    u.pitch = 1.0;
    u.volume = 1.0;
    u.onend = () => setAudioOn(false);
    u.onerror = () => setAudioOn(false);
    utteranceRef.current = u;
    s.speak(u);
    setAudioOn(true);
  };

  return (
    <div
      className="relative w-full h-[560px] rounded-md overflow-hidden border border-slate-200 bg-slate-900"
      data-testid="hero-showcase">
      {SLIDES.map((s, i) => (
        <div
          key={s.src}
          aria-hidden={i !== active}
          className="absolute inset-0 transition-opacity duration-[1200ms] ease-out"
          style={{ opacity: i === active ? 1 : 0 }}>
          <img
            src={s.src}
            alt={s.caption}
            loading={i === 0 ? "eager" : "lazy"}
            className={`w-full h-full object-cover object-top ${i === active ? "hero-kenburns" : ""}`}
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent px-6 pt-16 pb-6">
            <div className="flex items-center gap-2">
              <span className={`inline-block text-[10px] tracking-[0.22em] uppercase font-bold px-2 py-0.5 rounded-sm ${s.module === "RIF" ? "bg-blue-900 text-white" : "bg-rose-900 text-white"}`}>
                {s.module}
              </span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/70 font-semibold">
                Live app footage
              </span>
            </div>
            <div className="mt-1 text-white text-lg font-semibold drop-shadow-sm">{s.caption}</div>
          </div>
        </div>
      ))}

      {/* Progress ticks */}
      <div className="absolute top-4 left-4 right-24 flex gap-1.5">
        {SLIDES.map((_, i) => (
          <div key={i} className="h-0.5 flex-1 bg-white/30 rounded-sm overflow-hidden">
            <div
              className={`h-full bg-white ${i < active ? "w-full" : i === active ? "hero-tick" : "w-0"}`}
              style={{ animationDuration: `${SLIDE_MS}ms` }}
            />
          </div>
        ))}
      </div>

      {/* Audio toggle button — required for autoplay policies */}
      <button
        type="button"
        onClick={toggleAudio}
        aria-label={audioOn ? "Mute narration" : "Play narration"}
        title={audioOn ? "Mute" : "Play with narration"}
        className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/60 hover:bg-black/80 backdrop-blur-sm px-2.5 py-1.5 rounded-sm transition-colors"
        data-testid="hero-showcase-audio-toggle">
        {audioOn ? (
          <Volume2 className="h-3.5 w-3.5 text-white" strokeWidth={2} />
        ) : (
          <VolumeX className="h-3.5 w-3.5 text-white/80" strokeWidth={2} />
        )}
        <span className="text-[10px] tracking-[0.18em] uppercase text-white font-bold">
          {audioOn ? "Mute" : "Sound on"}
        </span>
      </button>

      {/* Live pill */}
      <div className="absolute top-3 right-24 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-2.5 py-1.5 rounded-sm pointer-events-none">
        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-[10px] tracking-[0.2em] uppercase text-white font-bold">Live</span>
      </div>

      <style>{`
        @keyframes hero-kenburns-kf {
          0%   { transform: scale(1.02) translateY(0); }
          100% { transform: scale(1.08) translateY(-1.2%); }
        }
        .hero-kenburns { animation: hero-kenburns-kf ${SLIDE_MS + 400}ms ease-out both; }
        @keyframes hero-tick-kf { from { width: 0%; } to { width: 100%; } }
        .hero-tick { animation: hero-tick-kf linear both; }
      `}</style>
    </div>
  );
}
